import { ICON_ADD, iconset } from "./iconset.js";
import __decorate from "../_virtual/_@oxc-project_runtime@0.147.0/helpers/esm/decorate.js";
import { LitElement, css, html, nothing, svg } from "lit";
import { property, state } from "lit/decorators.js";
//#region src/pd-icon/PdIcon.ts
function iconSvg(iconName, cssClass, stateValues, activAn, inActivAn) {
	return svg`
    <svg id="${iconName}Id" viewBox="0 0 24 24" class="${cssClass}">
      <polygon 
        id="polygonId" 
        points="${stateValues}"
      >
        <animate
          id="stateActivAn"
          dur="300ms"
          fill="freeze"
          begin="indefinite"
          attributeName="points"
          keySplines="0.25 0.1 0.25 1"
          values="${activAn}"
        ></animate>
        <animate
          id="stateInactivAn"
          dur="300ms"
          fill="freeze"
          begin="indefinite"
          attributeName="points"
          keySplines="0.25 0.1 0.25 1"
          values="${inActivAn}"
        ></animate>
      </polygon>
    </svg>`;
}
function iconScaleSvg(iconName, cssClass, stateValues) {
	return svg`<svg id="${iconName}Id" viewBox="0 0 24 24" class="${cssClass}">
      <g transform="translate(12, 12)">
        <g id="scaleGroup" transform="translate(-12, -12)">
          <polygon
            id="polygonId"
            points="${stateValues}"
          >
            <animateTransform
              id="stateActivAn"
              attributeName="transform"
              type="scale"
              from="0"
              to="1"
              dur="300ms"
              fill="freeze"
              begin="indefinite"
              keySplines="0.25 0.1 0.25 1"
              calcMode="spline"
            ></animateTransform>
            <animateTransform
              id="stateInactivAn"
              attributeName="transform"
              type="scale"
              from="1"
              to="0"
              dur="300ms"
              fill="freeze"
              begin="indefinite"
              keySplines="0.25 0.1 0.25 1"
              calcMode="spline"
            ></animateTransform>
          </polygon>
        </g>
      </g>
    </svg>`;
}
/** Static single-polygon render without ids/animations — used for the
*  fillLevel stack, where the icon is drawn twice in one shadow root. */
function iconStaticSvg(cssClass, points) {
	return svg`
    <svg viewBox="0 0 24 24" class="${cssClass}">
      <polygon points="${points}"></polygon>
    </svg>`;
}
/**
* SVG icon component with animation support.
*
* @tagname pd-icon
*
* @property {string} icon - Icon name from iconset. Default: `ICON_ADD`.
* @property {boolean} activeIcon - Whether icon is in active state.
* @property {number} fillLevel - 0–100: partially filled icon. Renders the
*   icon twice — base in `--pd-icon-col`, on top the fill in
*   `--pd-icon-col-active`, clipped to the given percentage (generalizes
*   `activeIcon`, which is fillLevel 0/100). Only for polygon-based iconset
*   icons; function-based icons ignore it. No animations in this mode.
*
* @cssprop --pd-icon-size - Icon size. Default: `2rem`.
* @cssprop --pd-icon-bg-col - Background color. Default: `transparent`.
* @cssprop --pd-icon-col - Icon fill color. Default: `var(--pd-default-dark-col)`.
* @cssprop --pd-icon-stroke-col - Icon stroke color. Default: `transparent`.
* @cssprop --pd-icon-stroke-width - Icon stroke width. Default: `1px`.
* @cssprop --pd-icon-transition - Transition timing for the host (background, size). Default: `0.5s ease-in-out`.
* @cssprop --pd-icon-fill-transition - Transition for the icon colour itself. Set to `none` to switch the colour fade off. Default: `fill 0.4s`.
* @cssprop --pd-icon-bg-col-active - Active state background. Default: `transparent`.
* @cssprop --pd-icon-col-active - Active state fill color. Default: `var(--pd-default-col)`.
* @cssprop --pd-icon-bg-col-hover - Hover state background. Default: inherits from `--pd-icon-bg-col`.
* @cssprop --pd-icon-col-hover - Hover state fill color. Default: `var(--pd-default-hover-col)`.
* @cssprop --pd-icon-col-disabled - Disabled state fill color. Default: `var(--pd-default-disabled-col)`.
* @cssprop --pd-icon-primary-col - Primary variant fill color. Default: `var(--pd-secondary-dark-col)`.
* @cssprop --pd-icon-col-primary-active - Primary active fill color. Default: `var(--pd-secondary-col)`.
* @cssprop --pd-icon-primary-bg-col - Primary variant background. Default: `transparent`.
* @cssprop --pd-icon-primary-bg-col-hover - Primary hover background. Default: inherits from `--pd-icon-primary-bg-col`.
* @cssprop --pd-icon-round-padding - Padding for round variant. Default: `0.5rem`.
*/
var PdIcon = class extends LitElement {
	constructor(..._args) {
		super(..._args);
		this.icon = ICON_ADD;
		this.activeIcon = false;
		this._svgIcon = nothing;
	}
	static {
		this.styles = [css`
      :host {
        --my-icon-size: var(--pd-icon-size, 2rem);
        --my-icon-bg-col: var(--pd-icon-bg-col, transparent);

        --my-icon-fill-col: var(--pd-icon-col, var(--pd-default-dark-col));
        --my-icon-stroke-col: var(--pd-icon-stroke-col, transparent);

        width: var(--my-icon-size);
        height: var(--my-icon-size);
        background-color: var(--my-icon-bg-col);
        display: inline-flex;
        box-sizing: content-box;
        vertical-align: sub;
        transition: var(--pd-icon-transition, 0.5s ease-in-out);
        align-items: center;
        justify-content: center;
        padding: 0.2rem;
      }

      :host([activeIcon]) {
        --my-icon-bg-col: var(--pd-icon-bg-col-active, transparent);
      }

      :host > svg {
        fill: var(--my-icon-fill-col);
        stroke: var(--my-icon-stroke-col);
        stroke-width: var(--pd-icon-stroke-width, 1px);
        /* Eigener Token: Der Host-Übergang oben deckt Hintergrund und
           Größe ab, dieser hier das Verblassen der Icon-Farbe. Beide
           getrennt abschaltbar; Default unverändert. */
        transition: var(--pd-icon-fill-transition, fill 0.4s);
        height: 100%;
        width: 100%;
      }

      :host(.primary) > svg {
        --my-icon-fill-col: var(
          --pd-icon-primary-col,
          var(--pd-secondary-dark-col)
        );
      }

      :host([activeIcon]) > svg {
        --my-icon-fill-col: var(--pd-icon-col-active, var(--pd-default-col));
      }

      :host(.primary[activeIcon]) > svg {
        --my-icon-fill-col: var(
          --pd-icon-col-primary-active,
          var(--pd-secondary-col)
        );
      }

      :host(.primary[activeIcon]:hover) > svg {
        --my-icon-fill-col: var(
          --pd-icon-col-hover,
          var(--pd-default-hover-col)
        );
      }

      :host(.small) {
        --my-icon-size: 1.25rem;
      }

      :host(.medium) {
        --my-icon-size: 1.5rem;
      }

      :host(.primary) {
        --my-icon-bg-col: var(--pd-icon-primary-bg-col, transparent);
      }

      :host(:hover) {
        --my-icon-bg-col: var(--pd-icon-bg-col-hover, var(--my-icon-bg-col));
      }

      :host(.primary:hover) {
        --my-icon-bg-col: var(
          --pd-icon-primary-bg-col-hover,
          var(--my-icon-bg-col)
        );
      }

      :host(:hover) svg {
        --my-icon-fill-col: var(
          --pd-icon-col-hover,
          var(--pd-default-hover-col)
        );
      }

      :host(.round) {
        border-radius: 50%;
        padding: var(--pd-icon-round-padding, 0.5rem);
        --my-icon-bg-col: var(--pd-icon-bg-col, var(--pd-default-lightest-col));
      }

      :host([disabled]) {
        pointer-events: none;
        box-shadow: none;
      }

      :host([disabled]) > svg {
        --my-icon-fill-col: var(
          --pd-icon-col-disabled,
          var(--pd-default-disabled-col)
        );
      }

      .arrow-next {
        transform: rotate(180deg);
      }

      .arrow-next {
        transform: rotate(180deg);
      }

      polygon {
        transform-origin: center;
        transform-box: fill-box;
      }

      /* fillLevel: base icon + clipped fill layer stacked on top. The
       * layers reuse the activeIcon color semantics (base --pd-icon-col,
       * fill --pd-icon-col-active). */
      .fill-stack {
        position: relative;
        width: 100%;
        height: 100%;
        display: inline-flex;
      }

      .fill-stack svg {
        /* block statt inline: das Overlay-SVG in .fill-clip würde sonst
         * auf der Text-Baseline landen und bei Kontext-Zeilenhöhen über
         * der Sterngröße nach unten rutschen (verrutschte Füll-Ebene). */
        display: block;
        fill: var(--my-icon-fill-col);
        stroke: var(--my-icon-stroke-col);
        stroke-width: var(--pd-icon-stroke-width, 1px);
        width: 100%;
        height: 100%;
      }

      .fill-clip {
        position: absolute;
        inset: 0;
        clip-path: inset(0 calc(100% - var(--_pd-icon-fill-level, 0%)) 0 0);
      }

      .fill-clip svg {
        fill: var(--pd-icon-col-active, var(--pd-default-col));
      }
    `];
	}
	firstUpdated() {
		this._animatePolygon();
	}
	update(changedProps) {
		if (changedProps.has("icon") && this.icon) {
			const iconDef = iconset[this.icon];
			if (!iconDef) throw new Error(`Icon not defined: ${this.icon}`);
			this._svgIcon = "icon" in iconDef ? iconDef.icon(this.activeIcon) : iconDef.useScale ? iconScaleSvg(this.icon, iconDef.cssClass, this.activeIcon ? iconDef.state.activ : iconDef.state.inactiv) : iconSvg(this.icon, iconDef.cssClass, this.activeIcon ? iconDef.state.activ : iconDef.state.inactiv, iconDef.state.activAn, iconDef.state.inActivAn);
		} else if (changedProps.has("activeIcon")) {
			const iconDef = iconset[this.icon];
			if (iconDef && "icon" in iconDef) this._svgIcon = iconDef.icon(this.activeIcon);
			this._animatePolygon();
		}
		super.update(changedProps);
	}
	render() {
		const fillStack = this._fillStack;
		if (fillStack) {
			const level = Math.max(0, Math.min(100, this.fillLevel));
			return html`
        <div class="fill-stack" style="--_pd-icon-fill-level: ${level}%">
          ${iconStaticSvg(fillStack.cssClass, fillStack.points)}
          <div class="fill-clip" aria-hidden="true">
            ${iconStaticSvg(fillStack.cssClass, fillStack.points)}
          </div>
        </div>
      `;
		}
		return html`${this._svgIcon}`;
	}
	/** fillLevel mode is only available for polygon-based iconset icons —
	*  function-based icons may carry ids/defs that must not be duplicated. */
	get _fillStack() {
		if (this.fillLevel === void 0 || this.fillLevel === null) return null;
		const iconDef = iconset[this.icon];
		if (!iconDef || !("state" in iconDef)) return null;
		return {
			cssClass: iconDef.cssClass,
			points: this.activeIcon ? iconDef.state.activ : iconDef.state.inactiv
		};
	}
	_animatePolygon() {
		const iconDef = iconset[this.icon];
		if (!iconDef || !("state" in iconDef)) return;
		const animId = this.activeIcon ? "stateActivAn" : "stateInactivAn";
		(this.shadowRoot?.getElementById(animId))?.beginElement();
	}
};
__decorate([property({ type: String })], PdIcon.prototype, "icon", void 0);
__decorate([property({
	type: Boolean,
	reflect: true
})], PdIcon.prototype, "activeIcon", void 0);
__decorate([property({ type: Number })], PdIcon.prototype, "fillLevel", void 0);
__decorate([state()], PdIcon.prototype, "_svgIcon", void 0);
//#endregion
export { PdIcon };
